import { useTheme } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { alertError, alertSuccess } from "../../../../components/alert/swal";
import {
  setLoading,
  unsetLoading,
} from "../../../../store/loading/loading.slice";
import ContainerLayout from "../../../../components/container-layout/ContainerLayout";
import StateComponents from "../../../../components/state-component/StateComponents";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { routeName } from "../../../../routes/routeName";
import { edpexResponsibleAPI } from "../../../../api/edpex-responsible/edpex-responsible.api";
import ContainerButton from "../../../../components/button/ContainerButton";
import AccordionUpload from "../../../../components/accordion/AccordionUpload";
import { edpexFormMasterAPI } from "../../../../api/edpex-form-master/edpex-form-master.api";

interface UploadedFile {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  file: File;
}

interface SubmitFormBody {
  form_type: string;
  edpex_form_name: string;
  status: string;
  id_files: number[];
}

const EdpexSampleSummaryData = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [name, setName] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [mode, setMode] = useState<string>("VIEW");

  const handleFileUpload = async (files: File[]): Promise<number[]> => {
    if (files.length === 0) return [];

    try {
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append("files", file);
      });

      const res = await edpexFormMasterAPI.uploadFileEdpexFormMaster(
        "FILE_IMAGE",
        formData
      );

      // Extract file IDs from the correct path: res.data.data
      const fileIds =
        res.data?.data?.map((file: any) => file.id_edpex_form_file) || [];
      return fileIds;
    } catch (error: any) {
      console.error("File upload error:", error);
      alertError(
        t("เกิดข้อผิดพลาด"),
        error?.response?.data?.message || t("อัปโหลดไฟล์ไม่สำเร็จ")
      );
      return [];
    }
  };

  // Step 2: Submit form with file IDs
  const handleSubmit = async (submitStatus: "active" | "เผยแพร่") => {
    // Validate form data
    if (!name.trim()) {
      alertError(t("เกิดข้อผิดพลาด"), t("กรุณากรอกชื่อ"));
      return;
    }

    try {
      dispatch(setLoading());

      // Step 1: Upload files first and get IDs
      const newFileIds = await handleFileUpload(
        uploadedFiles.map((file) => file.file).filter(Boolean)
      );

      const allFileIds = [
        ...uploadedFiles.map((file) => file.id).filter((id) => id),
        ...newFileIds,
      ];

      // Step 2: Submit form with file IDs
      // const body: SubmitFormBody = {
      //   form_type: "EXAMPLE_SUMMARY",
      //   edpex_form_name: name,
      //   status: submitStatus,
      //   id_files: allFileIds,
      // };

      const body: any = {
        form_type: "EXAMPLE_SUMMARY",
        edpex_form_name: name,
        status: submitStatus,
        id_files: allFileIds,
      };

      const res = await edpexFormMasterAPI.updateEdpexFormMaster(body);

      if (res.data.status < 300) {
        const successMessage =
          submitStatus === "active"
            ? t("บันทึกข้อมูลสำเร็จ")
            : t("เผยแพร่ข้อมูลสำเร็จ");

        alertSuccess(t("สำเร็จ"), successMessage);

        // Reset states and refresh data
        setMode("VIEW");
        await handleGetData();
      }
    } catch (error: any) {
      const errorMessage =
        submitStatus === "active"
          ? t("บันทึกข้อมูลไม่สำเร็จ")
          : t("เผยแพร่ข้อมูลไม่สำเร็จ");

      alertError(
        t("เกิดข้อผิดพลาด"),
        error?.response?.data?.message || errorMessage
      );
    } finally {
      dispatch(unsetLoading());
    }
  };

  const handleGetData = useCallback(async () => {
    try {
      dispatch(setLoading());

      const res = await edpexFormMasterAPI.getEdpexFormMasterByType({
        form_type: "EXAMPLE_SUMMARY",
        is_release: 0,
      });

      if (res.data.status < 300) {
        setName(res.data.data.edpex_form_name || "");
        // 
        const mappedFiles: UploadedFile[] = (
          res.data.data.document_files || []
        ).map((file: any) => ({
          id: file.id_edpex_form_file,
          name: file.file_name,
          size: `${(file.file_size / 1024).toFixed(2)} KB`,
          uploadDate: file.modified_date || file.created_date || "",
          file_path: file.file_path,
          file: null as unknown as File, // Placeholder since real File object is not available from API
        }));

        setUploadedFiles(mappedFiles);
      }
    } catch (error: any) {
      console.error("Get data error:", error);
      alertError(t("เกิดข้อผิดพลาด"), t("โหลดข้อมูลไม่สำเร็จ"));
    } finally {
      dispatch(unsetLoading());
    }
  }, [dispatch, t]);

  const handleCancel = () => {
    setMode("VIEW");
    handleGetData(); // Reload original data
  };

  useEffect(() => {
    handleGetData();
  }, [handleGetData]);

  return (
    <ContainerLayout
      title={t("จัดการข้อมูลตัวอย่างการสรุปผล")}
      hidden_sub_title
      navigateContent={
        <NavigateListCustom
          navigateData={[
            {
              key: "1",
              name: t("ระบบ Edpex"),
              disabled_match: true,
              pathName: routeName.cms.edpexResultDisplay,
            },
            {
              key: "2",
              name: t("จัดการข้อมูลตัวอย่างการสรุปผล"),
              pathName: routeName.cms.edpexSampleSummaryData,
            },
          ]}
        />
      }
      buttonContent={
        <ContainerButton
          justifyContent="flex-end"
          buttonData={
            mode === "VIEW"
              ? [
                  {
                    buttonProps: {
                      onClick: () => setMode("UPDATE"),
                    },
                    key: "EDIT",
                    label: t("แก้ไข"),
                    seq: 1,
                    type: "EDIT",
                  },
                ]
              : [
                  {
                    buttonProps: {
                      onClick: handleCancel,
                    },
                    key: "CANCEL",
                    label: t("ยกเลิก"),
                    seq: 1,
                    type: "CANCEL",
                  },
                  {
                    buttonProps: {
                      onClick: () => handleSubmit("active"),
                    },
                    key: "SAVE",
                    label: t("บันทึก"),
                    seq: 2,
                    type: "ADD2",
                  },
                  {
                    buttonProps: {
                      onClick: () => handleSubmit("เผยแพร่"), // i18n-ignore (submit mode value)
                    },
                    key: "PUBLISH",
                    label: t("เผยแพร่"),
                    seq: 3,
                    type: "PUBLISH",
                  },
                ]
          }
        />
      }
      content={
        <>
          <div className="w-full flex justify-between">
            <span className="flex-1">
              <StateComponents
                disabledAllField={mode === "VIEW"}
                typeProps={[
                  {
                    seq: 1,
                    type: "TEXT_FIELD",
                    gridProps: {
                      xs: 12,
                      md: 12,
                    },
                    fieldProps: {
                      value: name,
                      placeholder: t("ชื่อ"),
                      onChange: (e) => setName(e.target.value),
                      required: true,
                    },
                  },
                ]}
              />
            </span>
          </div>

          <br />

          <AccordionUpload
            defaultExpanded
            required
            enableButtonLeft
            titleName={t("ไฟล์แนบ")}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            acceptedFileTypes=".pdf,.doc,.docx,.jpg,.png,.xlsx"
            maxFileSize={5} // 5MB limit
            multiple={true}
            isView={mode === "VIEW"}
            // onFileUpload={handleFileUpload} // Pass the upload handler if needed by component
            // onRemoveFile={handleRemoveFile} // Pass the remove handler if needed
          />
        </>
      }
    />
  );
};

export default EdpexSampleSummaryData;
